import { useEffect, useRef } from 'react';

/**
 * CockpitModal — reusable cockpit-styled modal chrome.
 *
 * Props:
 *   open       — boolean
 *   title      — string rendered in the modal header (uppercased via CSS)
 *   onClose    — called when ESC pressed or backdrop clicked
 *   footer     — React node rendered at the bottom (typically a row of CockpitModalButtons)
 *   children   — modal body content
 *
 * Behavior:
 *   - Locks background scroll while open.
 *   - ESC key closes.
 *   - Backdrop (outside-click) closes.
 *   - Inside-click does not propagate to the backdrop (content is safe to interact with).
 */
export function CockpitModal({ open, title, onClose, footer, children }) {
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) { if (e.key === 'Escape') onClose && onClose(); }
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="FIG-cpe-modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose && onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div ref={boxRef} className="FIG-cpe-modal" onMouseDown={(e) => e.stopPropagation()}>
        {title && <div className="FIG-cpe-modal-title">{title}</div>}
        <div className="FIG-cpe-modal-body">{children}</div>
        {footer && <div className="FIG-cpe-modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * CockpitModalButton — cockpit-styled button for modal footers.
 * variant: 'default' (amber) | 'cancel' (dim gray) | 'destructive' (red)
 */
export function CockpitModalButton({ variant = 'default', onClick, disabled, children, type = 'button' }) {
  const cls = 'FIG-cpe-modal-btn FIG-cpe-modal-btn--' + variant;
  return (
    <button type={type} className={cls} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}
